import { Radar, Sparkles, Target } from "lucide-react";
import { Lbl, Num, Section } from "@/components/rcc/wf/Controls";
import { SCORE_CATS, WEIGHTS, summarize, weightShares } from "@/lib/workflowBuilder";

const W_CLS = ["text-zinc-600", "text-zinc-400", "text-[#c79bff]", "text-[#F3C649]", "text-orange-300"];

export const StepScore = ({ c, put }) => {
  const w = c.scoring.weights; const share = weightShares(w);
  return (
    <div className="space-y-4">
      <Section title="How important is each factor?" hint="The score is always 0-100. Factors with more weight move it more." testid="wf-weights">
        {SCORE_CATS.map(([k, l, hint]) => (
          <div key={k} className="grid items-center gap-2 border-t border-[#1f1f23] py-2 first:border-t-0 sm:grid-cols-[1fr_220px_48px]" data-testid={`wf-weight-${k}`}>
            <div><p className="text-xs text-zinc-200">{l}</p><p className="text-[10px] text-zinc-500">{hint}</p></div>
            <div><input type="range" min={0} max={4} step={1} value={w[k] ?? 2} onChange={(e) => put(`scoring.weights.${k}`, Number(e.target.value))} className="w-full accent-[#8224e3]" data-testid={`wf-weight-${k}-range`} />
              <p className={`text-[10px] ${W_CLS[w[k] ?? 2]}`} data-testid={`wf-weight-${k}-label`}>{WEIGHTS[w[k] ?? 2]}</p></div>
            <span className="text-right font-mono text-[11px] text-zinc-500">{share[k]}%</span>
          </div>))}
      </Section>
      <Section title="Hot opportunity line" testid="wf-hot">
        <div className="flex items-center gap-2 text-xs text-zinc-300">Mark as Hot and write a "Why Now?" at score <span className="w-20"><Num value={c.scoring.hot_threshold} onChange={(v) => put("scoring.hot_threshold", v)} testid="wf-hot-threshold" /></span> or higher</div>
      </Section>
    </div>
  );
};

const Example = () => (
  <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-b from-[#D4AF37]/10 to-transparent p-4" data-testid="wf-example-result">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">What a result looks like (example)</p>
    <div className="mt-2 flex items-center gap-3"><span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#D4AF37] font-mono text-lg text-[#F3C649]">91</span>
      <div><p className="text-sm font-semibold text-zinc-100">Acme Analytics</p><p className="text-[11px] text-zinc-400">RPO Opportunity Score 91/100 · Hot</p></div></div>
    <p className="mt-3 text-[11px] font-semibold text-zinc-300">Why this scored highly</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-zinc-400"><li>17 open positions, 8 in GTM</li><li>Series B raised 41 days ago</li><li>New CRO started 3 weeks ago</li><li>4 roles reposted in 30 days</li></ul>
    <p className="mt-3 text-[11px] font-semibold text-zinc-300">Why Now?</p>
    <p className="text-[11px] text-zinc-400">Acme has stepped up GTM hiring right after its Series B and a new CRO, with several sales roles reposted. These signals point to recruiting demand this quarter.</p>
  </div>
);

export const StepReview = ({ meta, c }) => (
  <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
    <Section title={meta.name || "Untitled workflow"} hint={`Runs: ${meta.schedule || "daily 07:00"}`} testid="wf-review">
      <dl className="space-y-1.5 text-xs">{summarize(c).map(([k, v]) => <div key={k} className="flex gap-3" data-testid={`wf-review-${k.toLowerCase()}`}><dt className="w-24 shrink-0 text-zinc-500">{k}</dt><dd className="text-zinc-200">{v}</dd></div>)}</dl>
      <Lbl>After each run</Lbl>
      <ul className="space-y-1.5 text-xs text-zinc-300">
        <li className="flex gap-2"><Radar className="h-4 w-4 shrink-0 text-[#D4AF37]" />Matching companies go into Opportunity Radar with their score.</li>
        <li className="flex gap-2"><Sparkles className="h-4 w-4 shrink-0 text-[#D4AF37]" />Each shows "Why this scored highly" (real evidence only) and, at {c.scoring.hot_threshold}+, a "Why Now?".</li>
        {c.people.auto_find && <li className="flex gap-2"><Target className="h-4 w-4 shrink-0 text-[#D4AF37]" />Decision makers are found for companies scoring {c.people.min_score}+.</li>}
      </ul>
    </Section>
    <Example />
  </div>
);
